import { httpClient } from '@/services/http/httpClient';
import type {
  Post,
  PaginatedPosts,
  PostStats,
  ListPostsParams,
  ScheduleParams,
  UpdatePostDto,
  BulkApproveDto,
  ContentGenerationDto,
  PromptContentGenerationDto,
  ContentGenerationResult,
  ContentJobStatus,
} from '@/types';

// ── Backend → Frontend normalisation ────────────────────────────────

/** The raw shape the BE actually returns (nested content, Buffer IDs). */
interface RawPost {
  _id: string | { buffer: { data: number[] } };
  userId?: string | { buffer: { data: number[] } };
  platform: string;
  status: string;
  content?: { text?: string; hashtags?: string[]; mediaUrls?: string[] };
  // Legacy flat fields some endpoints may still use
  text?: string;
  hashtags?: string[];
  imageUrl?: string;
  cta?: string;
  scheduledFor?: string | null;
  pillarId?: string;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
  media?: string[];
  /** Permalink to the published post on the external network, when available */
  platformSpecificMeta?: { platformUrl?: string } | null;
  [key: string]: unknown;
}

/** Convert a MongoDB Buffer id `{ buffer: { data: [bytes] } }` to a hex string. */
function bufferIdToHex(id: string | { buffer: { data: number[] } }): string {
  if (typeof id === 'string') return id;
  if (id?.buffer?.data) return id.buffer.data.map(b => b.toString(16).padStart(2, '0')).join('');
  return String(id);
}

/**
 * Normalise a raw BE post into the canonical frontend Post shape.
 * Handles: Buffer ObjectIds, nested `content` bag, envelope leftovers.
 */
function normalizePost(raw: unknown): Post {
  // Handle envelope that wasn't stripped (missing meta/timestamp)
  const obj = (
    raw != null && typeof raw === 'object' && 'data' in raw && !(raw as unknown as RawPost)._id
      ? (raw as { data: RawPost }).data
      : raw
  ) as RawPost;

  return {
    _id: bufferIdToHex(obj._id),
    userId: obj.userId ? bufferIdToHex(obj.userId) : '',
    platform: obj.platform,
    text: obj.content?.text ?? obj.text ?? '',
    hashtags: obj.content?.hashtags ?? obj.hashtags ?? [],
    status: obj.status,
    scheduledFor: obj.scheduledFor ?? null,
    pillarId: obj.pillarId,
    source: obj.source,
    imageUrl: obj.content?.mediaUrls?.[0] ?? obj.imageUrl,
    mediaUrls: obj.content?.mediaUrls ?? obj.media ?? (obj.imageUrl ? [obj.imageUrl] : []),
    cta: obj.cta,
    // BE exposes the external permalink under platformSpecificMeta.platformUrl (LIL-412)
    url: obj.platformSpecificMeta?.platformUrl,
    createdAt: obj.createdAt,
    updatedAt: obj.updatedAt,
  };
}

/** Normalise an array of raw posts. */
function normalizePosts(raw: unknown[]): Post[] {
  return raw.map(normalizePost);
}

// ── API methods ─────────────────────────────────────────────────────

export const postsApi = {
  /** GET /api/posts — paginated list with optional filters */
  async list(params?: ListPostsParams) {
    const result = await httpClient.get<PaginatedPosts>('/posts', { params });
    return { ...result, data: normalizePosts((result.data ?? []) as unknown[]) };
  },

  /** GET /api/posts/stats — post statistics (user derived from Bearer token) */
  async getStats() {
    return httpClient.get<PostStats>('/posts/stats');
  },

  /** GET /api/posts/schedule — posts within a date range */
  async getSchedule(params: ScheduleParams) {
    const result = await httpClient.get<Post[]>('/posts/schedule', { params });
    return normalizePosts(Array.isArray(result) ? result : []);
  },

  /** GET /api/posts/:id — single post */
  async getById(id: string) {
    return normalizePost(await httpClient.get(`/posts/${id}`));
  },

  /** PATCH /api/posts/:id — update post fields */
  async update(id: string, data: UpdatePostDto) {
    return normalizePost(await httpClient.patch(`/posts/${id}`, data));
  },

  /** PATCH /api/posts/:id/reschedule — re-sync scheduled time with PostForMe */
  async reschedule(id: string, scheduledFor: string) {
    return normalizePost(
      await httpClient.patch(`/posts/${id}/reschedule`, { scheduledFor })
    );
  },

  /**
   * DELETE /api/integrations/social/posts/:postId/schedule — tear down the
   * scheduled PostForMe job. Returns 200 with no meaningful payload; the
   * caller is responsible for flipping the post status locally.
   */
  async unschedule(id: string) {
    return httpClient.delete(`/integrations/social/posts/${id}/schedule`);
  },

  /** POST /api/posts/:id/approve — approve a single post */
  async approve(id: string) {
    return normalizePost(await httpClient.post(`/posts/${id}/approve`));
  },

  /** DELETE /api/posts/:id — permanently delete a post */
  async delete(id: string) {
    return httpClient.delete(`/posts/${id}`);
  },

  /** POST /api/posts/bulk-approve — approve multiple posts */
  async bulkApprove(data: BulkApproveDto) {
    return httpClient.post<{ approved: string[] }>('/posts/bulk-approve', data);
  },

  /** POST /api/agents/content/generate — queue AI content generation */
  async generateContent(data: ContentGenerationDto) {
    return httpClient.post<ContentGenerationResult>('/agents/content/generate', data);
  },

  /** POST /api/agents/content/generate-prompt — generate from user prompt */
  async generateFromPrompt(data: PromptContentGenerationDto) {
    return httpClient.post<ContentGenerationResult>('/agents/content/generate-prompt', data);
  },

  /** GET /api/agents/content/job/:jobId — poll generation job status */
  async getJobStatus(jobId: string) {
    return httpClient.get<ContentJobStatus>(`/agents/content/job/${jobId}`);
  },
};
